import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useImpersonation } from "@/hooks/useImpersonation";
import {
  useInactivityTimeout,
  bumpActivity,
  clearActivity,
} from "@/hooks/useInactivityTimeout";
import {
  broadcastLogout,
  subscribeAuthEvents,
} from "@/lib/sessionBroadcast";

/**
 * Mounts once inside <AuthProvider>. Enforces the 24-hour inactivity rule
 * for any signed-in user, syncs logouts across tabs, and forces logout when
 * a mobile/PWA returns from background after the threshold.
 */
export default function SessionActivityGuard() {
  const { user, signOut } = useAuth();
  const { isImpersonating } = useImpersonation();
  const navigate = useNavigate();
  const loggingOutRef = useRef(false);

  // Disabled for guests and during admin impersonation.
  const enabled = !!user && !isImpersonating;

  const handleTimeout = async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    clearActivity();
    broadcastLogout("inactivity");
    try {
      await signOut();
    } finally {
      navigate("/session-expired", { replace: true });
      loggingOutRef.current = false;
    }
  };

  const handleWarn = () => {
    toast.warning("You'll be signed out in 5 minutes due to inactivity.", {
      duration: 30_000,
      action: {
        label: "Stay signed in",
        onClick: () => bumpActivity(),
      },
    });
  };

  useInactivityTimeout({
    enabled,
    onTimeout: handleTimeout,
    onWarn: handleWarn,
  });

  // Cross-tab logout listener
  useEffect(() => {
    if (!enabled) return;
    const unsub = subscribeAuthEvents(async (evt) => {
      if (evt.type !== "logout" || loggingOutRef.current) return;
      loggingOutRef.current = true;
      clearActivity();
      try {
        await signOut();
      } finally {
        navigate(
          evt.reason === "inactivity" ? "/session-expired" : "/login",
          { replace: true }
        );
        loggingOutRef.current = false;
      }
    });
    return unsub;
  }, [enabled, signOut, navigate]);

  return null;
}
